import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertCircle, Filter, RefreshCw, Search, ServerCrash, User } from 'lucide-react';

import { api } from '@/services/api';
import { RecentVisitor, RecentVisitors, SessionTimeline, TimelineEvent } from '@/types';

/**
 * Sessions: what one visitor actually experienced.
 *
 * Left: recent visitors (optionally only those who hit errors), with how far
 * they got. Right: the chosen visitor's timeline — every page view, step,
 * save, blocked click and browser error, merged with backend errors recorded
 * for their consultation, in order. Search by consultation, visitor or
 * session id to jump straight to a support case.
 */

/** Row source, e.g. "vfs / qr / sfo_office", "www.google.com" or "direct". The
 * API attributes it like the Traffic sources table; older builds send only
 * referrerHost. */
function sourceLabel(v: RecentVisitor): string {
  if (!v.source) return v.referrerHost ?? 'direct';
  if (v.source === '(direct)') return 'direct';
  return [v.source, v.medium, v.campaign].filter(Boolean).join(' / ');
}

const WINDOW_OPTIONS: { value: number | 'all'; label: string }[] = [
  { value: 1, label: 'Last 24 hours' },
  { value: 7, label: 'Last 7 days' },
  { value: 30, label: 'Last 30 days' },
  { value: 'all', label: 'All time' },
];

const ERROR_EVENTS = new Set([
  'api_error', 'js_error', 'server_error', 'form_cta_blocked', 'payment_failed',
  'trip_start_failed', 'summary_load_failed',
]);
const MILESTONES = new Set([
  'city_search_select', 'summary_view', 'summary_unlocked_view', 'signup_success', 'signin_success',
  'traveler_count_set', 'payment_page_view', 'payment_submit', 'payment_success', 'partner_click',
]);

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function fmtTime(iso: string) {
  const d = new Date(iso);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`;
}

function describe(e: TimelineEvent): string {
  const p = (e.props ?? {}) as Record<string, unknown>;
  switch (e.name) {
    case 'page_view': return `Viewed ${e.path ?? e.route ?? ''}`;
    case 'page_engaged': return `Spent ${p.seconds ?? '?'}s on ${e.route ?? ''}, scrolled ${p.max_depth ?? '?'}%`;
    case 'scroll_depth': return `Scrolled ${p.depth}% of ${e.route ?? ''}`;
    case 'api_error': return `API ${p.method ?? ''} ${p.endpoint ?? ''} failed (${p.status})${p.request_id ? ` · request ${p.request_id}` : ''}`;
    case 'js_error': return `Browser error on ${e.route ?? ''}: ${p.message ?? ''}`;
    case 'form_cta_blocked': return `Clicked disabled Next on "${p.section ?? ''}" · missing: ${p.missing ?? '?'}`;
    case 'server_error': return `${e.lambda ?? 'server'} ${e.method ?? ''} ${e.route ?? ''} → ${e.status} ${e.errorClass ?? ''}${e.message ? `: ${e.message}` : ''} · request ${e.requestId ?? ''}`;
    case 'web_vital': return `${p.name}: ${p.value} (${p.rating})`;
    default: {
      const bits = Object.entries(p).filter(([k]) => k !== 'category').map(([k, v]) => `${k}=${String(v)}`).join(', ');
      return `${e.name.replace(/_/g, ' ')}${bits ? ` · ${bits}` : ''}`;
    }
  }
}

export default function SessionsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [days, setDays] = useState<number | 'all'>(7);
  const [errorsOnly, setErrorsOnly] = useState(searchParams.get('errors') === '1');
  const [includeInternal, setIncludeInternal] = useState(false);
  const [visitors, setVisitors] = useState<RecentVisitor[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [query, setQuery] = useState(searchParams.get('consultation_id') || searchParams.get('visitor_id') || '');
  const [timeline, setTimeline] = useState<SessionTimeline | null>(null);
  const [tlLoading, setTlLoading] = useState(false);
  const [tlError, setTlError] = useState('');
  const [showAll, setShowAll] = useState(false);

  const loadVisitors = async () => {
    setListLoading(true);
    setListError('');
    try {
      const res: RecentVisitors = await api.getRecentVisitors({
        days,
        ...(errorsOnly ? { errors: 1 as const } : {}),
        ...(includeInternal ? { internal: 1 as const } : {}),
        limit: 100,
      });
      setVisitors(res.visitors);
    } catch (err: any) {
      setListError(err.response?.data?.error || err.message || 'Failed to load visitors');
    } finally {
      setListLoading(false);
    }
  };

  const loadTimeline = async (lookup: { visitor_id?: string; session_id?: string; consultation_id?: string }): Promise<SessionTimeline | null> => {
    setTlLoading(true);
    setTlError('');
    try {
      const res: SessionTimeline = await api.getSessionTimeline(lookup);
      setTimeline(res);
      setSearchParams(lookup as Record<string, string>);
      return res;
    } catch (err: any) {
      setTimeline(null);
      setTlError(err.response?.data?.error || err.message || 'Failed to load timeline');
      return null;
    } finally {
      setTlLoading(false);
    }
  };

  useEffect(() => {
    loadVisitors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, errorsOnly, includeInternal]);

  useEffect(() => {
    const cid = searchParams.get('consultation_id');
    const vid = searchParams.get('visitor_id');
    const sid = searchParams.get('session_id');
    if (cid) loadTimeline({ consultation_id: cid });
    else if (vid) loadTimeline({ visitor_id: vid });
    else if (sid) loadTimeline({ session_id: sid });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const id = query.trim();
    if (!UUID.test(id)) {
      setTlError('Enter a consultation id, visitor id or session id (a UUID).');
      return;
    }
    // Support tickets carry consultation ids; try that first, then visitor, then session.
    void (async () => {
      for (const lookup of [{ consultation_id: id }, { visitor_id: id }, { session_id: id }]) {
        const res = await loadTimeline(lookup);
        if (res && res.events.length > 0) return;
      }
      setTlError('Nothing recorded for that id in any of consultation, visitor or session.');
    })();
  };

  const events = timeline?.events ?? [];
  const shown = showAll ? events : events.filter((e) => e.name !== 'scroll_depth' && e.name !== 'web_vital');

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sessions</h1>
          <p className="text-gray-500 mt-1">What one visitor actually experienced: pages, steps, errors, and backend failures, in order</p>
        </div>
        <form onSubmit={onSearch} className="flex items-center gap-2 mt-4 sm:mt-0">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Consultation, visitor or session id"
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-80 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
          <button type="submit" className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">
            <Search size={16} className="mr-2" />
            Open
          </button>
        </form>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Visitors list */}
        <div className="bg-white rounded-lg shadow-sm xl:col-span-1">
          <div className="flex flex-wrap items-center gap-3 px-5 py-4 border-b border-gray-100">
            <User size={16} className="text-gray-400" />
            <h2 className="text-sm font-semibold text-gray-900 flex-1">Recent visitors</h2>
            <label className="inline-flex items-center gap-1.5 text-xs text-gray-600">
              <input type="checkbox" checked={errorsOnly} onChange={(e) => setErrorsOnly(e.target.checked)} className="rounded border-gray-300" />
              Errors only
            </label>
            <label className="inline-flex items-center gap-1.5 text-xs text-gray-600" title="Browsers flagged with ?internal=1 are hidden by default">
              <input type="checkbox" checked={includeInternal} onChange={(e) => setIncludeInternal(e.target.checked)} className="rounded border-gray-300" />
              Include team traffic
            </label>
            <div className="flex items-center gap-1">
              <Filter size={14} className="text-gray-400" />
              <select
                value={String(days)}
                onChange={(e) => setDays(e.target.value === 'all' ? 'all' : Number(e.target.value))}
                className="border border-gray-300 rounded-md px-2 py-1 text-xs"
              >
                {WINDOW_OPTIONS.map((o) => <option key={String(o.value)} value={String(o.value)}>{o.label}</option>)}
              </select>
            </div>
            <button onClick={loadVisitors} className="text-gray-500 hover:text-gray-700" title="Refresh">
              <RefreshCw size={14} />
            </button>
          </div>
          {listError && <p className="px-5 py-3 text-sm text-red-700">{listError}</p>}
          {listLoading ? (
            <div className="flex items-center justify-center py-10"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" /></div>
          ) : listError ? null : visitors.length === 0 ? (
            <p className="px-5 py-6 text-sm text-gray-500">
              No visitors in this window.{!includeInternal && ' Team browsers (flagged with ?internal=1) are hidden; tick "Include team traffic" to see your own testing.'}
            </p>
          ) : (
            <ul className="divide-y divide-gray-50 max-h-[70vh] overflow-y-auto">
              {visitors.map((v) => (
                <li key={v.visitorId}>
                  <button
                    onClick={() => loadTimeline(v.consultationId ? { consultation_id: v.consultationId } : { visitor_id: v.visitorId })}
                    className={`w-full text-left px-5 py-3 hover:bg-gray-50 ${timeline?.visitorIds.includes(v.visitorId) ? 'bg-primary-50' : ''}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-900 truncate">{v.lastRoute ?? '(no page view)'}</span>
                      <span className="flex items-center gap-2 text-xs">
                        {v.paid && <span className="px-1.5 py-0.5 rounded bg-green-100 text-green-800">paid</span>}
                        {v.errors > 0 && <span className="px-1.5 py-0.5 rounded bg-red-100 text-red-800">{v.errors} err</span>}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {fmtTime(v.lastSeen)} · {v.pageViews} pages · {v.device ?? '?'} · {sourceLabel(v)}
                      {v.consultationId && <span className="ml-1 text-gray-400">· consultation {v.consultationId.slice(0, 8)}…</span>}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Timeline */}
        <div className="bg-white rounded-lg shadow-sm xl:col-span-2">
          <div className="flex flex-wrap items-center gap-3 px-5 py-4 border-b border-gray-100">
            <h2 className="text-sm font-semibold text-gray-900 flex-1">
              Timeline
              {timeline && (
                <span className="ml-2 font-normal text-gray-500">
                  {timeline.counts.browser} browser events · {timeline.counts.server} backend errors · {timeline.counts.errors} problems
                  {timeline.truncated ? ' · truncated' : ''}
                </span>
              )}
            </h2>
            <label className="inline-flex items-center gap-1.5 text-xs text-gray-600">
              <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} className="rounded border-gray-300" />
              Show scroll and speed events
            </label>
          </div>
          {tlError && (
            <div className="flex items-center gap-2 px-5 py-3 text-sm text-red-700"><AlertCircle size={16} />{tlError}</div>
          )}
          {tlLoading ? (
            <div className="flex items-center justify-center py-10"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" /></div>
          ) : !timeline ? (
            <p className="px-5 py-10 text-sm text-gray-500 text-center">Pick a visitor on the left, or paste a consultation id from a support request.</p>
          ) : shown.length === 0 ? (
            <p className="px-5 py-6 text-sm text-gray-500">No events found for this id.</p>
          ) : (
            <ol className="divide-y divide-gray-50 max-h-[70vh] overflow-y-auto">
              {shown.map((e, i) => {
                const isError = ERROR_EVENTS.has(e.name);
                const isMilestone = MILESTONES.has(e.name);
                return (
                  <li key={i} className={`px-5 py-2.5 flex gap-4 text-sm ${isError ? 'bg-red-50' : isMilestone ? 'bg-green-50' : ''}`}>
                    <span className="w-40 shrink-0 text-xs text-gray-500 tabular-nums pt-0.5">{fmtTime(e.at)}</span>
                    <span className="w-5 shrink-0 pt-0.5">
                      {e.kind === 'server' ? <ServerCrash size={14} className="text-red-600" /> : isError ? <AlertCircle size={14} className="text-red-500" /> : null}
                    </span>
                    <span className={`flex-1 ${isError ? 'text-red-900' : 'text-gray-800'}`}>
                      <span className="font-medium">{e.name.replace(/_/g, ' ')}</span>
                      <span className="text-gray-600"> — {describe(e)}</span>
                      {e.internal && <span className="ml-2 text-[10px] uppercase text-gray-400">team</span>}
                    </span>
                  </li>
                );
              })}
            </ol>
          )}
          {timeline && (
            <p className="px-5 py-3 text-xs text-gray-400 border-t border-gray-50">
              Looked up by {Object.entries(timeline.lookup).map(([k, v]) => `${k} ${v}`).join(', ')} · visitors {timeline.visitorIds.join(', ') || '—'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
